"use client";
/*
 * Documentation:
 * Badge — https://app.subframe.com/library?component=Badge_97bdb082-1124-4dd7-a335-b14b822d0157
 */

import React from "react";
import * as SubframeCore from "@subframe/core";
import * as SubframeUtils from "../utils";

interface BadgeRootProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "brand" | "neutral" | "error" | "warning" | "success";
  source?: "general" | "invoice" | "bill" | "transaction" | "journal";
  status?: "open" | "in_progress" | "closed" | "resolved";
  priority?: "low" | "medium" | "high";
  icon?: React.ReactNode;
  children?: React.ReactNode;
  iconRight?: React.ReactNode;
  className?: string;
}

const BadgeRoot = React.forwardRef<HTMLDivElement, BadgeRootProps>(
  function BadgeRoot(
    {
      variant = "brand",
      source = "general",
      status = "open",
      priority = "low",
      icon = null,
      children,
      iconRight = null,
      className,
      ...otherProps
    }: BadgeRootProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "group/97bdb082 flex h-6 items-center gap-1 rounded-md border border-solid border-brand-100 bg-brand-100 px-2",
          {
            "border border-solid border-success-100 bg-success-100":
              source === "general" || variant === "success",
            "border border-solid border-warning-100 bg-warning-100":
              source === "invoice" || variant === "warning",
            "border border-solid border-error-100 bg-error-100":
              source === "bill" || variant === "error",
            "border border-solid border-neutral-100 bg-neutral-100":
              source === "transaction" || variant === "neutral",
            "border border-solid border-neutral-100 bg-neutral-101":
              source === "journal",
          },
          className
        )}
        ref={ref}
        {...otherProps}
      >
        {icon ? (
          <SubframeCore.IconWrapper
            className={SubframeUtils.twClassNames(
              "text-caption font-caption text-brand-700",
              {
                "text-success-800": status === "open",
                "text-warning-800": status === "in_progress",
                "text-error-700": status === "closed",
                "text-neutral-700": status === "resolved",
              }
            )}
          >
            {icon}
          </SubframeCore.IconWrapper>
        ) : null}
        {children ? (
          <span
            className={SubframeUtils.twClassNames(
              "whitespace-nowrap text-caption font-caption text-brand-800",
              {
                "text-success-800": priority === "low",
                "text-warning-800": priority === "high" || variant === "error",
                "text-neutral-700": priority === "medium",
              }
            )}
          >
            {children}
          </span>
        ) : null}
        {iconRight ? (
          <SubframeCore.IconWrapper
            className={SubframeUtils.twClassNames(
              "text-caption font-caption text-brand-700",
              {
                "text-success-800": priority === "low",
                "text-warning-800": priority === "high",
                "text-neutral-700": priority === "medium",
              }
            )}
          >
            {iconRight}
          </SubframeCore.IconWrapper>
        ) : null}
      </div>
    );
  }
);

export const Badge = BadgeRoot;
